/**
 * `/support` — the Support URL.
 *
 * Authorised by `docs/marketing/app-store-listing.md` §URLs: "**Support URL** | Required.
 * Must resolve and offer a contact route. `apps/web` is the natural host."
 *
 * Three sections, in the order they are useful: how to write something that can be acted
 * on, the form itself, and the two requests that need saying out loud — a data request,
 * and an accessibility fault. The accessibility section exists because
 * design-direction's sensory laws are product commitments, and a commitment with no
 * reporting route is a preference.
 *
 * A direct address sits right under the intro (RULINGS R141): a dedicated
 * `support@usecarn.app`, forwarding to the founder, named as the Art. 13 contact detail
 * this page and `/privacy` both carry. The form stays the primary route; the address is
 * the one a reader who has lost the device, or does not trust a web form, still has.
 */
import { PageFrame, Section } from './PageFrame';
import { SupportForm } from './SupportForm';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { text } from './prose';

export function SupportPage() {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <PageFrame current="/support" titleKey="support.title">
      <p style={{ ...text(theme.type.body), color: c.textSecondary, margin: 0 }}>
        {t('support.intro')}
      </p>
      {/* RULINGS R141: the dedicated address, named directly as the Art. 13 contact
          detail rather than left implicit in where the form's notification goes. */}
      <p style={{ ...text(theme.type.body), color: c.textSecondary, margin: 0 }}>
        {t('support.direct')}
      </p>

      <Section titleKey="support.include.title" bodyKeys={['support.include.body']} />

      <section style={{ display: 'flex', flexDirection: 'column', gap: s.md }}>
        <h2
          style={{
            ...text(theme.type.title),
            fontSize: theme.type.title.fontSize * 0.7,
            color: c.text,
            margin: 0,
          }}
        >
          {t('support.form.title')}
        </h2>
        <SupportForm />
      </section>

      {/* Erasure is a control in the app now (docs/RULINGS.md R134), so this section names it —
          and keeps this page as the route for the person whose device is gone, which is the one
          case the in-app control cannot serve. */}
      <Section
        titleKey="support.data.title"
        bodyKeys={['support.data.body', 'support.data.erase', 'support.data.lostDevice']}
      />
      <Section
        titleKey="support.accessibility.title"
        bodyKeys={['support.accessibility.body']}
      />
    </PageFrame>
  );
}
